import { useMemo } from "react";
import type { ReactNode } from "react";
import { TreeSelect, Tooltip } from "antd";
import type { TreeSelectProps } from "antd/lib/tree-select";
import type { Layer } from "@blings/blings-player";
import { InfoCircleOutlined } from "@ant-design/icons";
import editorTools from "@blings/blings-player/editor-tools";
import { truncateMiddle } from "../../../utils/string";

import type { PropertyDocumentation } from "@blings/blings-player/editor-tools";

const {
  describeLayerPropertiesAPI: {
    describeLayerProperties,
    describeLayerProperty,
  },
} = editorTools;

type TreeDataNode = NonNullable<TreeSelectProps["treeData"]>[number];

interface LayerPropertyTreeSelectProps {
  layer?: Layer | null;
  value?: string;
  disabled?: boolean;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
}

type BuilderNode = {
  segment: string;
  path: string;
  doc?: PropertyDocumentation;
  children: Map<string, BuilderNode>;
};

const PATH_SEGMENT_REGEX = /([^[\].]+|\[\d+\])/g;
const ARRAY_INDEX_REGEX = /^\[(\d+)\]$/;

const splitPath = (path: string): string[] =>
  (path.match(PATH_SEGMENT_REGEX) ?? []).map((segment) => {
    const arrayMatch = segment.match(ARRAY_INDEX_REGEX);
    if (arrayMatch) return arrayMatch[1];
    return segment;
  });

const joinSegments = (segments: string[]): string =>
  segments.filter(Boolean).join(".");

const normalizePathValue = (path?: string): string | undefined => {
  if (!path) return undefined;
  const segments = splitPath(path);
  if (!segments.length) return undefined;
  return joinSegments(segments);
};

const formatNodeLabel = (
  doc: PropertyDocumentation | undefined,
  fallback: string
): string => {
  const sanitizedFallback = fallback.replace(ARRAY_INDEX_REGEX, "$1");
  const preferred =
    (doc?.title &&
      doc.title.trim() &&
      doc.title.trim() !== "*" &&
      doc.title.trim()) ||
    (doc?.name &&
      doc.name.trim() &&
      doc.name.trim() !== "*" &&
      doc.name.trim()) ||
    sanitizedFallback;
  return preferred;
};

const getDocKey = (doc?: PropertyDocumentation): string | undefined =>
  ((doc as PropertyDocumentation & { nm?: string })?.nm ??
    doc?.name ??
    doc?.path) ||
  undefined;

const formatOptionValue = (value: unknown): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (value === null) return "null";
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

type EnumOption = {
  key: string;
  value: string;
  label?: string;
};

type OptionSection = {
  title: string;
  options: EnumOption[];
};

const trimLabel = (value?: string): string | undefined =>
  value?.trim() || undefined;

type RawOption = {
  rawValue: unknown;
  label?: string;
};

const normalizeOptionList = (
  prefix: string,
  docPath: string | undefined,
  entries?: Array<RawOption | undefined>
): EnumOption[] | undefined => {
  if (!entries?.length) return undefined;
  const options = entries
    .filter((entry): entry is RawOption => Boolean(entry))
    .map((entry, index) => {
      const value = formatOptionValue(entry.rawValue);
      if (!value) return undefined;
      return {
        key: `${prefix}-${docPath ?? index}-${value}-${index}`,
        value,
        label: trimLabel(entry.label),
      };
    })
    .filter((option): option is EnumOption => Boolean(option));

  return options.length ? options : undefined;
};

const getEnumOptions = (
  doc?: PropertyDocumentation
): EnumOption[] | undefined => {
  if (!doc?.enumValues?.length) return undefined;
  return normalizeOptionList(
    "enum",
    doc.path,
    doc.enumValues.map((enumValue) => ({
      rawValue: enumValue.value,
      label: enumValue.label,
    }))
  );
};

type DocWithFlags = PropertyDocumentation & {
  flags?:
    | Array<{
        value?: unknown;
        flag?: unknown;
        label?: string;
        description?: string;
      }>
    | Record<
        string,
        | string
        | { label?: string; description?: string }
        | number
        | boolean
        | null
      >;
};

const getFlagLabel = (value: unknown): string | undefined => {
  if (typeof value === "string") return trimLabel(value);
  if (!value || typeof value !== "object") return undefined;
  const { label, description } = value as {
    label?: unknown;
    description?: unknown;
  };
  if (typeof label === "string") return trimLabel(label);
  if (typeof description === "string") return trimLabel(description);
  return undefined;
};

const getFlagOptions = (
  doc?: PropertyDocumentation
): EnumOption[] | undefined => {
  const rawFlags = (doc as DocWithFlags | undefined)?.flags;
  if (!rawFlags) return undefined;

  if (Array.isArray(rawFlags)) {
    const entries = rawFlags.map((item, index) => {
      if (!item || typeof item !== "object") return undefined;
      const rawValue =
        "value" in item
          ? item.value
          : "flag" in item
          ? (item as { flag?: unknown }).flag
          : index;
      return { rawValue, label: getFlagLabel(item) };
    });
    return normalizeOptionList("flag", doc?.path, entries);
  }

  if (rawFlags && typeof rawFlags === "object") {
    const entries = Object.entries(rawFlags).map(([key, value]) => ({
      rawValue: key,
      label: getFlagLabel(value),
    }));
    return normalizeOptionList("flag", doc?.path, entries);
  }

  return undefined;
};

const renderOptionsSection = (
  title: string,
  options?: EnumOption[],
  hasSectionAbove?: boolean
): ReactNode => {
  if (!options?.length) return null;
  return (
    <div style={{ marginTop: hasSectionAbove ? 6 : 0 }}>
      <strong>{title}</strong>
      <ul style={{ paddingLeft: 16, margin: "4px 0" }}>
        {options.map((option) => (
          <li key={option.key}>
            <code>{option.value}</code>
            {option.label ? ` – ${option.label}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
};

const formatDisplayableValue = (value: unknown): string | undefined => {
  if (value === undefined) return undefined;
  if (isPlainObject(value)) return undefined;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) {
    try {
      return JSON.stringify(value);
    } catch {
      return undefined;
    }
  }
  if (value === null) return "null";
  return undefined;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isObjectLike = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getValueAtPath = (source: unknown, path: string): unknown => {
  if (!path) return undefined;
  return splitPath(path).reduce<unknown>((acc, segment) => {
    if (acc == null) return undefined;

    if (isObjectLike(acc) && segment in acc) {
      return (acc as Record<string, unknown>)[segment];
    }

    return undefined;
  }, source);
};

const createSyntheticDoc = (
  path: string,
  value: unknown,
  label?: string
): PropertyDocumentation => {
  const normalizedLabel = label || path;
  return {
    path,
    name: normalizedLabel,
    title: normalizedLabel,
    required: false,
    isFallback: true,
    description: undefined,
    type: Array.isArray(value) ? "array" : typeof value,
    defaultValue: undefined,
    enumValues: undefined,
    constValue: undefined,
    examples: undefined,
    variantTitle: undefined,
    variantDescription: undefined,
    value,
    valueType: Array.isArray(value)
      ? "array"
      : value === null
      ? "null"
      : typeof value,
    valueLabel: undefined,
  };
};

const expandPropertyDocumentation = (
  layer: Layer,
  docs: PropertyDocumentation[]
): PropertyDocumentation[] => {
  if (typeof describeLayerProperty !== "function") return docs;

  const docsByPath = new Map<string, PropertyDocumentation>();
  const queue: PropertyDocumentation[] = [];

  const ensureDocForPath = (
    targetPath: string,
    fallbackValue: unknown,
    label?: string
  ) => {
    if (!targetPath || docsByPath.has(targetPath)) return;

    let childDoc: PropertyDocumentation | undefined;
    try {
      childDoc = describeLayerProperty(layer, targetPath);
    } catch (error) {
      console.warn(
        `[LayerPropertyTreeSelect] Failed to describe property ${targetPath}`,
        error
      );
    }

    if (!childDoc) {
      childDoc = createSyntheticDoc(targetPath, fallbackValue, label);
    } else {
      const normalizedPath = normalizePathValue(childDoc.path) ?? targetPath;
      if (childDoc.path !== normalizedPath) {
        childDoc = { ...childDoc, path: normalizedPath };
      }
    }

    if (childDoc?.path && !docsByPath.has(childDoc.path)) {
      docsByPath.set(childDoc.path, childDoc);
      queue.push(childDoc);
    }
  };

  docs.forEach((doc) => {
    const normalizedPath = normalizePathValue(doc.path);
    if (!normalizedPath) return;
    const normalizedDoc =
      normalizedPath === doc.path ? doc : { ...doc, path: normalizedPath };
    docsByPath.set(normalizedPath, normalizedDoc);
    queue.push(normalizedDoc);
  });

  while (queue.length) {
    const current = queue.shift()!;
    if (!current.path) continue;

    const sourceValue =
      (isPlainObject(current.value) && current.value) ||
      (current.path ? getValueAtPath(layer, current.path) : undefined);

    const parentSegments = current.path ? splitPath(current.path) : [];

    if (isPlainObject(sourceValue)) {
      Object.keys(sourceValue).forEach((key) => {
        const childPath = joinSegments([...parentSegments, key]);
        ensureDocForPath(childPath, sourceValue[key], key);
      });
      continue;
    }

    if (Array.isArray(sourceValue)) {
      sourceValue.forEach((item, index) => {
        const childPath = joinSegments([...parentSegments, `${index}`]);
        ensureDocForPath(childPath, item, `${index}`);
      });
      continue;
    }
  }

  return Array.from(docsByPath.values());
};

const renderDocTooltip = (doc: PropertyDocumentation): ReactNode => {
  const description = doc.description?.trim();
  const docKey = getDocKey(doc);
  const displayableValue = formatDisplayableValue(doc.value);
  const hasValueInfo =
    !isPlainObject(doc.value) &&
    (displayableValue !== undefined || doc.valueLabel);
  const enumOptions = getEnumOptions(doc);
  const flagOptions = getFlagOptions(doc);
  const optionSections: OptionSection[] = [
    { title: "Available values:", options: enumOptions },
    { title: "Flags:", options: flagOptions },
  ].filter((section): section is OptionSection =>
    Boolean(section.options?.length)
  );
  const hasValueSection = hasValueInfo || optionSections.length > 0;

  return (
    <div style={{ maxWidth: 280 }}>
      <div>
        <em>{doc.title || doc.name || doc.path}</em>
        {docKey && <span> (key: {docKey})</span>}
      </div>
      <div style={{ marginTop: 4 }}>
        <strong>Description:</strong>
        <div style={{ whiteSpace: "pre-wrap" }}>
          {description || "No description available."}
        </div>
      </div>
      {doc.valueType && (
        <div style={{ marginTop: 4 }}>
          <code>{`<${doc.valueType}>`}</code>
        </div>
      )}
      {hasValueSection && (
        <>
          <div style={{ margin: "6px 0" }}>---</div>
          {hasValueInfo && (
            <div>
              <strong>Current Value:</strong>{" "}
              {displayableValue ?? "Not provided"}
              {doc.valueLabel ? ` (${doc.valueLabel})` : ""}
            </div>
          )}
          {optionSections.map((section, index) =>
            renderOptionsSection(
              section.title,
              section.options,
              hasValueInfo || index > 0
            )
          )}
        </>
      )}
    </div>
  );
};

const renderNodeTitle = (
  label: string,
  doc: PropertyDocumentation | undefined
): ReactNode => {
  if (!doc) return label;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <span>{label}</span>
      <Tooltip title={renderDocTooltip(doc)} placement="top">
        <InfoCircleOutlined
          style={{ fontSize: 12, color: "#8c8c8c" }}
          onClick={(event) => {
            event.stopPropagation();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
        />
      </Tooltip>
    </span>
  );
};

type LabeledTreeDataNode = TreeDataNode & {
  fullLabel: string;
  displayLabel: string;
};

const buildTree = (docs: PropertyDocumentation[]): LabeledTreeDataNode[] => {
  const root: BuilderNode = {
    segment: "",
    path: "",
    children: new Map(),
  };

  docs.forEach((doc) => {
    if (!doc.path) return;
    const segments = splitPath(doc.path);
    if (!segments.length) return;

    const traversed: string[] = [];
    let current = root;

    segments.forEach((segment, index) => {
      traversed.push(segment);
      const nodePath = joinSegments(traversed);
      if (!current.children.has(segment)) {
        current.children.set(segment, {
          segment,
          path: nodePath,
          children: new Map(),
        });
      }

      const node = current.children.get(segment)!;
      node.path = nodePath;
      if (index === segments.length - 1) {
        node.doc = doc;
      }
      current = node;
    });
  });

  const toTreeData = (
    node: BuilderNode,
    parentLabels: string[]
  ): LabeledTreeDataNode => {
    const nodeLabel = formatNodeLabel(node.doc, node.segment);
    const nextLabels = nodeLabel ? [...parentLabels, nodeLabel] : parentLabels;
    const fullLabel = nextLabels.join(" - ") || nodeLabel || node.path;
    const displayLabel = truncateMiddle(fullLabel);

    const children =
      node.children.size > 0
        ? Array.from(node.children.values()).map((child) =>
            toTreeData(child, nextLabels)
          )
        : undefined;

    const dataNode: LabeledTreeDataNode = {
      title: renderNodeTitle(nodeLabel, node.doc),
      key: node.path || node.segment,
      value: node.path || node.segment,
      selectable: Boolean(node.doc),
      fullLabel,
      displayLabel,
    };

    if (children) {
      dataNode.children = children;
    }
    return dataNode;
  };

  return Array.from(root.children.values()).map((child) =>
    toTreeData(child, [])
  );
};

export function LayerPropertyTreeSelect({
  layer,
  value,
  onChange,
  disabled,
  placeholder,
}: LayerPropertyTreeSelectProps) {
  const treeData = useMemo(() => {
    if (!layer) return [];
    try {
      const docs = describeLayerProperties(layer) ?? [];
      const expandedDocs = expandPropertyDocumentation(layer, docs);
      return buildTree(expandedDocs);
    } catch (error) {
      console.error(
        "[LayerPropertyTreeSelect] Failed to describe layer",
        error
      );
      return [];
    }
  }, [layer]);

  const props: TreeSelectProps<string> = {
    showSearch: true,
    allowClear: true,
    size: "large",
    treeData,
    value: value || undefined,
    disabled: disabled || !layer,
    placeholder:
      placeholder ||
      (layer ? "Select a property path" : "Select a layer to pick a path"),
    treeNodeFilterProp: "fullLabel",
    treeDefaultExpandAll: false,
    dropdownStyle: { maxHeight: 400, overflow: "auto" },
    treeNodeLabelProp: "displayLabel",
    style: { width: "100%", minWidth: 280, maxWidth: "100%" },
    onChange: (nextValue) => {
      const normalized = typeof nextValue === "string" ? nextValue : undefined;
      onChange(normalized);
    },
  };

  return <TreeSelect {...props} />;
}
